import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { UserPlus, UserCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function FollowButton({ handle, onCountChange, className = "", testId = "follow-btn" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!handle) return;
    api.get(`/filmmakers/${handle}/follow`).then((r) => {
      setState(r.data);
      onCountChange?.(r.data.follower_count);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, user?.id]);

  if (!state || state.is_self) return null;

  const toggle = async () => {
    if (!user) {
      toast("Log in to follow filmmakers");
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    setBusy(true);
    try {
      const { data } = state.following
        ? await api.delete(`/filmmakers/${handle}/follow`)
        : await api.post(`/filmmakers/${handle}/follow`);
      setState(data);
      onCountChange?.(data.follower_count);
      if (data.following) toast.success("Following — we'll email you when they release a new film");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const Icon = busy ? Loader2 : state.following ? UserCheck : UserPlus;
  return (
    <Button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={`rounded-full h-11 px-6 transition-colors ${state.following
        ? "bg-white/5 border border-white/20 text-foreground hover:bg-white/10"
        : "bg-gold hover:bg-gold/90 text-ink font-semibold"} ${className}`}
      data-testid={testId}
      data-following={state.following ? "true" : "false"}
    >
      <Icon className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
      {state.following ? "Following" : "Follow"}
    </Button>
  );
}
